// Content Rewrite Service - Rewrite/enhance audioguide descriptions
import { aiService } from './aiService';

const REWRITE_SYSTEM_PROMPT = `You are Sofia, a witty and knowledgeable audio guide. Your task is to rewrite audioguide stop descriptions so they sound like a real person talking, not a brochure.

**WORD COUNT:** 300-450 words. Stop when the story is done. No filler. A tight 250 words beats a padded 450.

**TONE & STYLE:**
- Write for the ear, not the eye. Fluid transitions. If a sentence sounds like a travel brochure, delete it.
- Be sarcastic but genuinely informative. Use modern common sense to judge historical events.
- Talk in first person and tell stories. Do not describe things like a catalogue.
- Somewhere near the start, ground the visitor: tell them where they are or what they're looking at, naturally.
- Use dates to provide context, but never let them lead the sentence. Not "In 1755, the earthquake struck" but "By 1755, when the earthquake finally hit..."
- No lists of facts. We want meaning, not curricula.

**CONTENT STRUCTURE:**
1. Hook: where are they, what is this about? Start the story here.
2. The Meat: why does this place exist, how did it work, what went wrong?
3. Human Detail: a specific anecdote, a weird coincidence, an insider fact.
4. The Wrap: a quick insight the visitor won't find on a plaque.

**STRICT CONSTRAINTS:**
- Never use: "represents," "stands as," "testament to," "must-see," "stunning," "breathtaking," "hidden gem," "flex"
- Never start with "Welcome to..." or "You are standing at..."
- Never use em-dashes or en-dashes. Use commas, periods, or colons.
- Never mention influencers, Instagram, TikTok, or hashtags.
- No bold, no bullet points, no formatting in the output text.
- Do not take political sides. You can mention political context if relevant, but never promote one ideology.
- Do not mention current prices or costs.
- Address the listener as an individual (singular "you"), not a group.

**Your Response:** ONLY the rewritten spoken text. No introductions, no commentary, no titles.`;

export const contentRewriteService = {
  /**
   * Rewrites all inDepthDescriptions to be more engaging and natural
   * @param {Array} points - Array of POI objects
   * @param {string} modelId - AI model to use
   * @param {string} language - Language code (e.g. 'en', 'pt-PT')
   * @returns {Promise<Array>} - Updated POIs with rewritten descriptions
   */
  async rewriteAllDescriptions(points, modelId = 'gpt-4', language = 'en') {
    try {
      if (!points || points.length === 0) {
        throw new Error('No points of interest provided for rewriting');
      }

      // Filter points with inDepthDescription
      const validPoints = points.filter(
        p => p.inDepthDescription && p.inDepthDescription.trim().length > 0
      );

      if (validPoints.length === 0) {
        throw new Error('No descriptions found to rewrite');
      }

      console.log(`Starting rewrite of ${validPoints.length} descriptions in language: ${language}...`);

      // Rewrite descriptions in parallel for efficiency
      const rewritePromises = validPoints.map(async (poi) => {
        try {
          const systemPrompt = `${REWRITE_SYSTEM_PROMPT}\n\n**LANGUAGE:** Respond in ${language}. Do NOT translate from the original — maintain the original language unless the original is in a different language than the target.`;

          const messages = [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: poi.inDepthDescription
            }
          ];

          const rewrittenText = await aiService.getCompletion(messages, modelId, {
            maxTokens: 8192,
            temperature: 0.7
          });

          return {
            ...poi,
            inDepthDescription: rewrittenText.trim()
          };
        } catch (error) {
          console.error(`Failed to rewrite description for ${poi.name}:`, error);
          // Return original if rewrite fails
          return poi;
        }
      });

      const rewrittenPoints = await Promise.all(rewritePromises);

      console.log(`✅ Rewrite complete! ${rewrittenPoints.length} descriptions updated.`);

      return rewrittenPoints;
    } catch (error) {
      console.error('Content rewrite failed:', error);
      throw error;
    }
  },

  async generateAllShortDescriptions(points, itineraryMetadata, modelId = 'gpt-4', language = 'en') {
    try {
      if (!points || points.length === 0) {
        throw new Error('No points of interest provided');
      }

      const languageMap = {
        'pt-PT': 'Write in European Portuguese (Portugal). ',
        'pt-BR': 'Write in Brazilian Portuguese. ',
        'es-ES': 'Write in Castilian Spanish. ',
        'es-MX': 'Write in Mexican Spanish. ',
        'en-US': 'Write in American English. ',
        'en-GB': 'Write in British English. ',
        'fr-FR': 'Write in French. ',
        'de-DE': 'Write in German. ',
        'it-IT': 'Write in Italian. ',
        'ja-JP': 'Write in Japanese. ',
        'zh-CN': 'Write in Simplified Chinese. ',
        'zh-TW': 'Write in Traditional Chinese. ',
      };

      const langPrefix = languageMap[language] || '';

      console.log(`Generating short descriptions for ${points.length} stops in language: ${language}...`);

      const updatedPoints = [];

      for (let i = 0; i < points.length; i++) {
        const poi = points[i];
        try {
          if (!poi.inDepthDescription) {
            updatedPoints.push(poi);
            continue;
          }

          // Truncate to ~2000 chars to avoid token limits
          const truncatedDescription = poi.inDepthDescription.substring(0, 2000);
          const prompt = `${langPrefix}Summarize this in-depth description in 1-2 concise sentences, aiming for 35-45 words total and about 2/3 of the original length. Keep only the most important facts:\n\n${truncatedDescription}`;

          const messages = [
            {
              role: 'system',
              content: 'Return ONLY the summary text. No titles, no meta-commentary.'
            },
            {
              role: 'user',
              content: prompt
            }
          ];

          const result = await aiService.getCompletion(messages, modelId, {
            maxTokens: 120,
            temperature: 0.7
          });

          updatedPoints.push({ ...poi, description: result.trim() });
        } catch (error) {
          console.error(`Failed to generate description for ${poi.name}:`, error);
          updatedPoints.push(poi);
        }
      }
      console.log(`✅ Short descriptions generated for ${updatedPoints.length} stops.`);
      return updatedPoints;
    } catch (error) {
      console.error('Short description generation failed:', error);
      throw error;
    }
  }
};
